import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import DataTable1 from '~/Common-Objects/Tables/DataTable1/DataTable1'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import PagingBlock from '~/Common-Objects/Tables/DataTablePaging/DataTablePaging'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import axios from '~/data/http/axios'
import moment from 'moment'
import { alterSnackbar } from '~/data/actions/siteActions'
import DateTimeFunctions from '~/Classes/dateTimeFunctions'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'

export default function LandingPageVisitors({ landingPage, LandingPageId, setLandingPage, saveLandingPage }) {
    const dispatch = useDispatch()
    const [limit, setLimit] = useState(30)
    const [page, setPage] = useState(0)
    const [sources, setSources] = useState(null)
    const [email, setEmail] = useState('')
    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(1, 'month').startOf('day'),
        endDate: moment().endOf('day')
    })
    const [visitors, setVisitors] = useState('')
    const [device, setDevice] = useState('')
    const [source, setSource] = useState('')
    const [hideRobots, setHideRobots] = useState(true)
    const [total, setTotal] = useState(null)
    const [data, setData] = useState(null)

    const getData = async () => {
        setData(null)
        let q =
            '?startDate=' +
            dateRange.startDate.toISOString() +
            '&endDate=' +
            dateRange.endDate.toISOString() +
            '&limit=' +
            limit +
            '&offset=' +
            page * limit
        if (device) q += '&device=' + device
        if (source) q += '&source=' + source
        if (visitors) q += '&visitors=' + visitors
        if (email) q += '&emailAddress=' + email.toLowerCase()
        if (hideRobots) q += '&hideRobots=true'

        try {
            const res = await axios.get('/landingPage/' + LandingPageId + '/visitors' + q)
            setTotal(res.data.total)
            setData(res.data.rows)
        } catch (e) {
            dispatch(alterSnackbar(true, 'Error fetching visitors'))
        }
        isFirstLoad.current = false
    }

    const updateDates = (dates) => {
        let startDate = dateRange.startDate
        let endDate = dateRange.endDate
        if (dates.startDate) startDate = dates.startDate.startOf('day')
        if (dates.endDate) endDate = dates.endDate.endOf('day')
        setDateRange({ startDate, endDate })
    }

    const isFirstLoad = useRef(true)

    useEffect(() => {
        if (isFirstLoad.current) {
            getData()
            return
        }
        if (page !== 0) {
            setPage(0)
        } else {
            getData()
        }
    }, [dateRange, device, source, visitors, hideRobots, limit])

    useEffect(() => {
        if (!isFirstLoad.current) {
            getData()
        }
    }, [page])

    useEffect(() => {
        if (isFirstLoad.current) return
        const timer = setTimeout(() => {
            if (page === 0) {
                getData()
            } else if (page !== 0) {
                setPage(1)
            }
        }, 500)
        return () => clearTimeout(timer)
    }, [email])

    useEffect(() => {
        axios.get('/landingPage/' + LandingPageId + '/visitors/sources').then((res) => {
            setSources(res.data)
        })
    }, [])

    if (!sources) return <LoadingIcons />

    const rows = (data || []).map((row) => {
        return {
            emailAddress: {
                headerValue: 'Visitor',
                value: row.SubscriberId ? (
                    <Link to={'/cp/subscriber/' + row.SubscriberId} target='_blank'>
                        {row.emailAddress}
                    </Link>
                ) : (
                    'Anonymous'
                )
            },
            lastVisit: {
                headerValue: 'Last Visit',
                value: DateTimeFunctions.formatDateTime(row.createdAt)
            },
            submitted: {
                headerValue: 'Submitted',
                value: DateTimeFunctions.formatDateTime(row.submittedDate)
            },
            country: {
                headerValue: 'Country',
                value: row.country || 'Unknown'
            },
            visits: {
                headerValue: 'Visits',
                value: row.visits || 0
            },
            browser: {
                headerValue: 'Browser',
                value: row.browser || 'Unknown'
            },
            device: {
                headerValue: 'Device',
                value: row.device || 'Unknown'
            },
            source: {
                headerValue: 'Source',
                value: row.source || ''
            }
        }
    })

    return (
        <Paper className='paper-padding-24'>
            <div className='row' style={{ marginBottom: 16 }}>
                <div className='col-xs-12 col-sm-6 col-md-4 col-lg-3'>
                    <TextInput
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        iconLeft={<Icons icon='magnifier' />}
                        placeholder='Search for Email Address'
                    />
                </div>
                <div className='col-xs-12 col-sm-6 col-md-8 col-lg-9 end-sm quickFlex'>
                    <Select
                        align='right'
                        inlineLabel='Per Page'
                        value={limit}
                        data={[10, 30, 50, 100]}
                        onChange={(e) => setLimit(e.target.value)}
                    />
                </div>
            </div>
            <div className='row' style={{ marginBottom: 16 }}>
                <div className='col-xs-12 col-sm-6 col-md-4 col-lg-3'>
                    <TextInput
                        outerClassName='mar-b25'
                        type='multiDate'
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        updateDate={updateDates}
                    />
                </div>
                <div className='col-xs-12 col-sm-6 col-md-8 col-lg-9'>
                    <div className='quickFlex' style={{ gap: 8 }}>
                        <Select
                            inlineLabel='Visitors'
                            value={visitors}
                            onChange={(e) => setVisitors(e.target.value)}
                            data={[
                                { value: '', label: 'All' },
                                { value: 'known', label: 'Recognised' },
                                { value: 'unknown', label: 'Anonymous' }
                            ]}
                        />
                        <Select
                            inlineLabel='Device'
                            value={device}
                            onChange={(e) => setDevice(e.target.value)}
                            data={[
                                { value: '', label: 'All' },
                                { value: 'mobile', label: 'Mobile' },
                                { value: 'desktop', label: 'Desktop' },
                                { value: 'tablet', label: 'Tablet' }
                            ]}
                        />
                        <Select
                            inlineLabel='Source'
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            data={[{ label: 'All', value: '' }].concat(sources.map((s) => ({ value: s, label: s })))}
                        />
                        <Toggle
                            checked={hideRobots}
                            onChange={() => setHideRobots(!hideRobots)}
                            after='Hide Robot Visits'
                        />

                        <div className='quickFlex' style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
                            {FormatNumberFuncs.formatNumber(total) + ' Visitors'}
                        </div>
                    </div>
                </div>
            </div>
            <DataTable1
                tableData={rows}
                loadingData={!data ? 1 : 0}
                pageSize={15}
                noResultsTxt='No landing page activity found'
            />
            {total > limit && (
                <div className='row' style={{ marginTop: 16 }}>
                    <div className='col-xs-12 center-xs'>
                        <PagingBlock
                            totalRows={total}
                            pageSize={limit}
                            numberOfLinks='10'
                            currentPage={page}
                            changePage={(p) => setPage(p)}
                            text={'Page:'}
                        />
                    </div>
                </div>
            )}
        </Paper>
    )
}
